/**
 * Kapitel „Webinare & Produkte" der Sektion „Anfos & Umsatz" (/auswertungen/webinare-produkte).
 * Umbau nach dem KPI-Review (Plan 2026-10-23-p1-p2-kpi-review-umbau, Spezifikation P1-V3): Seitenkopf mit drei Kacheln
 * (E1), Kartenreihenfolge Empfehlung → Webinare → Produkte → Effizienz → Upsell (E2), Erklärtext zugeklappt mit
 * Kopfzeile (E6). Persona-Umbau 28.10.2026 (Plan 2026-10-28-persona-umbau-paket-1-2, B2/B8): die Wörterzeile steht am
 * Seitenende (sie schob die Empfehlung unter die erste Bildschirmhöhe), die Karte „Neue Leads" (RegistrierungKarte) zieht
 * auf die P2-Unterseite „Vom neuen Lead zur Bestellung". Jede Karte lädt ihre Daten selbst — eine langsame Abfrage hält
 * die anderen nicht auf. Backend: /api/auswertungen/webinare-produkte/*.
 */
import { FC } from 'react';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import Woerterzeile from '../../../components/auswertungen/Woerterzeile';
import { LEITFRAGEN } from '../../../helpers/kpiDefinitions';
import Explainer, { ExplainerSection } from '../../../components/Explainer';
import { fetchProdukte, fetchProduktEffizienz, fetchWebinarRankingGeteilt } from '../../../helpers/auswertungenData';
import EffizienzKarte from './EffizienzKarte';
import ProdukteKarte from './ProdukteKarte';
import SeitenKopf from './SeitenKopf';
import UpsellKarte from './UpsellKarte';
// Workshop P (29.10.2026): A2 und W2.
import WertJeAnmeldungKarte from './WertJeAnmeldungKarte';
import WiederholungKarte from './WiederholungKarte';
import { KandidatenKarte, RankingKarte } from './WebinareKarten';
import { int, useLaden } from './shared';

const GRUNDLAGE: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'PL-X-Bestellungen und Zahlungen, Webinar-Anmeldungen und Teilnahme, die Empfängerzahlen der Webinar-Einladungen und Direkt-Mailings und der Anfo-Wert jeder Anfo. Die Geldgröße ist der Erwartete Wert (bei Bestellung): bewertet wird, dass bestellt wurde — mit dem heutigen Wert des Angebots, auch für ältere Termine. Echtes Geld steht als „Bisher bezahlt“ daneben. Gezählt werden dieselben Anfos wie überall in der Sektion (ohne Stornos und Testbestellungen); Anmeldungen nur live.',
  },
  {
    label: 'Berechnung',
    body: 'Zwei Zuordnungen stehen nebeneinander, nie im selben Bruch: alle Anfos, die einem Termin zugeordnet sind, und die Anfos über die Einladung (die erste Anfo einer Person, gebucht auf die Einladung, über die sie sich anmeldete). Nur die zweite lässt sich je Kontakt teilen. Vorläufig ergänzte Anfos zählen als Anfo, aber in keiner Quote und keinem Euro-Wert.',
  },
  {
    label: 'Wie gut die Prognose trifft',
    body: 'Im Rückblick (Backtest vom 20.10.2026) lagen 41 % der Termine in der mittleren Hälfte ihrer Prognose. Die Spanne ist also eher zu eng. Ab dem 01.11.2026 zählt die Karte „Was als Nächstes laufen sollte“ das laufend mit.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Kontaktzahlen gibt es für Webinar-Einladungen ab 10.12.2025, für Direkt-Mailings ab 23.12.2025 — der Vergleich der Produkte je 1.000 Kontakte beginnt deshalb für beide am 23.12.2025. Versände an die ganze Liste zählen nur mit ihren aktiven Empfängern. Junge Termine sind grau, bis ihre Zahlen nach 14 Tagen fertig sind. Alle Euro-Beträge netto (ohne MwSt.).',
  },
];

/** „Warum die Anfo-Zahlen je Karte verschieden sind" — die Zahlen live aus den Antworten der Karten. */
const useAnfoZahlen = (): ExplainerSection | null => {
  const r = useLaden(fetchWebinarRankingGeteilt);
  const p = useLaden(fetchProdukte);
  const e = useLaden(fetchProduktEffizienz);
  if (!r.data || !p.data || !e.data) return null;
  const s = p.data.summary;
  return {
    label: 'Warum die Anfo-Zahlen je Karte verschieden sind',
    body: `${int(s.webinar_anfos)} Anfos im Kanal Webinar · ${int(r.data.seitenkopf?.anfos ?? null)} einem Termin zugeordnet · ${int(e.data.summary.web.anfos)} über die Einladung (${int(e.data.webinare_im_fenster)} Termine im Kontaktfenster) · ${int(s.anfos)} Anfos aller Produkte${s.provisional > 0 ? ` (davon ${int(s.provisional)} vorläufig)` : ''}. Der Kanal kommt aus der Kennung im Bestell-Link, die Zuordnung zum Termin aus der Anmeldung; über die Einladung zählt nur die erste Anfo je Person und Termin.`,
  };
};

const WebinareProdukte: FC = () => {
  const zahlen = useAnfoZahlen();
  const abschnitte = zahlen ? [...GRUNDLAGE.slice(0, 2), zahlen, ...GRUNDLAGE.slice(2)] : GRUNDLAGE;
  return (
    <ChapterPage
      title="Webinare & Produkte"
      question={LEITFRAGEN.webinareProdukte}
      intro="Alle Euro-Beträge netto (ohne MwSt.)."
      standQuellen={[{ key: 'plx_orders', label: 'Bestellungen und Zahlungen (PL-X)' }, { key: 'webinar_zuordnung', label: 'Zuordnung zu Webinar-Terminen' }]}
    >
      <SeitenKopf />
      <Explainer title={`Wie dieses Kapitel rechnet — ${abschnitte.length} Abschnitte zum Nachlesen, nichts zu tun`} sections={abschnitte} />
      <KandidatenKarte />
      <RankingKarte />
      <WertJeAnmeldungKarte />
      <ProdukteKarte />
      <EffizienzKarte />
      <WiederholungKarte />
      <UpsellKarte />
      <Woerterzeile kapitel="webinareProdukte" />
    </ChapterPage>
  );
};

export default WebinareProdukte;
